"use client";

import { c as _c } from "react/compiler-runtime";
import SpinnerLoader from "@/components/common/SpinnerLoader";
import HeaderUserConnect from "@/components/header/user/HeaderUserConnect";
import WavePicture from "@/components/waves/WavePicture";
import { formatCount } from "@/helpers/format.helpers";
import { LockClosedIcon } from "@heroicons/react/24/outline";
import WaveScreenMessage from "../WaveScreenMessage";
import LoggedOutSkeleton from "./LoggedOutSkeleton";
import { type PublicWaveShellData, usePublicWaveShellState } from "./usePublicWaveShellState";
interface PublicWaveShellProps {
  readonly waveId: string;
}
function compactDescriptionText(text: string): string {
  const normalized = text.replaceAll(/https?:\/\/\S+/g, "").replaceAll(/\s+/g, " ").trim();
  if (normalized.length <= 240) {
    return normalized;
  }
  const shortened = normalized.slice(0, 237);
  const safeBoundary = shortened.lastIndexOf(" ");
  return `${shortened.slice(0, safeBoundary > 180 ? safeBoundary : 237)}...`;
}
function PublicWaveLoadingState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="tw-flex tw-min-h-full tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-py-8"><SpinnerLoader text="Loading wave..." /></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PublicWaveUnavailableState() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <WaveScreenMessage title="This wave isn't available publicly" description="Connect your wallet to check whether you have access." action={<HeaderUserConnect label="Connect Wallet" />} />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function PublicWaveShellContent(t0) {
  const $ = _c(42);
  const {
    wave
  } = t0;
  const t1 = wave.descriptionPreview ?? "";
  let t2;
  if ($[0] !== t1) {
    t2 = compactDescriptionText(t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const description = t2;
  const hasDescription = description.length > 0;
  let t3;
  if ($[2] !== wave.membersCount) {
    t3 = formatCount(wave.membersCount) ?? "0";
    $[2] = wave.membersCount;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const memberCount = t3;
  let t4;
  if ($[4] !== wave.postsCount) {
    t4 = formatCount(wave.postsCount) ?? "0";
    $[4] = wave.postsCount;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const postCount = t4;
  let t5;
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <LoggedOutSkeleton />;
    t6 = <div className="tw-pointer-events-none tw-absolute tw-left-1/2 tw-top-1/2 tw-size-96 -tw-translate-x-1/2 -tw-translate-y-1/2 tw-scale-125 tw-rounded-full tw-bg-white/10 tw-blur-3xl" />;
    $[6] = t5;
    $[7] = t6;
  } else {
    t5 = $[6];
    t6 = $[7];
  }
  let t7;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <div className="tw-absolute tw-inset-x-0 tw-top-0 tw-h-px tw-bg-gradient-to-r tw-from-transparent tw-via-white/10 tw-to-transparent tw-opacity-80" />;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className="tw-pointer-events-none tw-absolute tw-inset-0 tw-opacity-5 tw-mix-blend-overlay" style={{
      backgroundImage: "url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI0IiBoZWlnaHQ9IjQiPjxyZWN0IHdpZHRoPSI0IiBoZWlnaHQ9IjQiIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')"
    }} />;
    $[9] = t8;
  } else {
    t8 = $[9];
  }
  let t9;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  let t10;
  if ($[11] !== wave.name || $[12] !== wave.picture) {
    t10 = <div className="tw-size-12 tw-flex-shrink-0 tw-overflow-hidden tw-rounded-full tw-shadow-2xl tw-ring-1 tw-ring-white/10 tw-ring-offset-4 tw-ring-offset-iron-950 sm:tw-size-14"><WavePicture name={wave.name} picture={wave.picture} contributors={t9} /></div>;
    $[11] = wave.name;
    $[12] = wave.picture;
    $[13] = t10;
  } else {
    t10 = $[13];
  }
  let t11;
  if ($[14] !== wave.name) {
    t11 = <h2 className="tw-mb-2 tw-text-xl tw-font-semibold tw-leading-[1.05] tw-tracking-tighter tw-text-transparent tw-text-white sm:tw-text-2xl md:tw-text-3xl">{wave.name}</h2>;
    $[14] = wave.name;
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  let t12;
  if ($[16] !== t10 || $[17] !== t11) {
    t12 = <div className="tw-mb-1 tw-flex tw-max-w-md tw-flex-col tw-items-center tw-justify-center tw-gap-4">{t10}{t11}</div>;
    $[16] = t10;
    $[17] = t11;
    $[18] = t12;
  } else {
    t12 = $[18];
  }
  let t13;
  if ($[19] !== description || $[20] !== hasDescription) {
    t13 = hasDescription && <p className="tw-mb-10 tw-max-w-md tw-text-pretty tw-pb-0 tw-text-sm tw-font-normal tw-text-iron-400 md:tw-text-md">{description}</p>;
    $[19] = description;
    $[20] = hasDescription;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  let t14;
  if ($[22] !== memberCount) {
    t14 = <span className="tw-text-lg tw-font-semibold tw-text-white md:tw-text-xl">{memberCount}</span>;
    $[22] = memberCount;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-iron-500">Joined</span>;
    $[24] = t15;
  } else {
    t15 = $[24];
  }
  let t16;
  if ($[25] !== t14) {
    t16 = <div className="tw-flex tw-w-20 tw-flex-col tw-items-center tw-gap-1.5">{t14}{t15}</div>;
    $[25] = t14;
    $[26] = t16;
  } else {
    t16 = $[26];
  }
  let t17;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <div className="tw-h-8 tw-w-px tw-bg-iron-800" />;
    $[27] = t17;
  } else {
    t17 = $[27];
  }
  let t18;
  if ($[28] !== postCount) {
    t18 = <span className="tw-text-lg tw-font-semibold tw-text-white md:tw-text-xl">{postCount}</span>;
    $[28] = postCount;
    $[29] = t18;
  } else {
    t18 = $[29];
  }
  let t19;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <span className="tw-text-[10px] tw-font-bold tw-uppercase tw-tracking-widest tw-text-iron-500">Posts</span>;
    $[30] = t19;
  } else {
    t19 = $[30];
  }
  let t20;
  if ($[31] !== t18) {
    t20 = <div className="tw-flex tw-w-20 tw-flex-col tw-items-center tw-gap-1.5">{t18}{t19}</div>;
    $[31] = t18;
    $[32] = t20;
  } else {
    t20 = $[32];
  }
  let t21;
  if ($[33] !== t16 || $[34] !== t20) {
    t21 = <div className="tw-mb-8 tw-flex tw-w-full tw-items-center tw-justify-center tw-gap-8 tw-px-4 md:tw-gap-12">{t16}{t17}{t20}</div>;
    $[33] = t16;
    $[34] = t20;
    $[35] = t21;
  } else {
    t21 = $[35];
  }
  let t22;
  let t23;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="tw-relative tw-w-full tw-overflow-hidden tw-rounded-xl"><HeaderUserConnect className="tw-h-12 tw-w-full" icon={<LockClosedIcon className="tw-size-5 tw-flex-shrink-0" strokeWidth={2.5} />} label="Connect Wallet" /></div>;
    t23 = <p className="tw-mx-auto tw-mb-0 tw-mt-6 tw-max-w-sm tw-text-xs tw-font-semibold tw-tracking-wide tw-text-iron-600">This content is only available to connected wallets. Connect your wallet to continue.</p>;
    $[36] = t22;
    $[37] = t23;
  } else {
    t22 = $[36];
    t23 = $[37];
  }
  let t24;
  if ($[38] !== t12 || $[39] !== t13 || $[40] !== t21) {
    t24 = <div className="tw-relative tw-flex tw-h-full tw-min-h-0 tw-min-w-0 tw-flex-col tw-overflow-hidden tw-bg-black"><div className="tw-relative tw-min-h-0 tw-flex-1 tw-overflow-hidden">{t5}{t6}<div className="tw-absolute tw-inset-0 tw-z-10 tw-overflow-y-auto tw-px-4 tw-py-8 tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300 lg:tw-px-8"><div className="tw-flex tw-min-h-full tw-items-center tw-justify-center"><section className="tw-group tw-relative tw-z-10 tw-flex tw-w-full tw-max-w-lg tw-flex-col tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-white/5 tw-bg-iron-950 tw-shadow-2xl">{t7}{t8}<div className="tw-relative tw-z-10 tw-flex tw-flex-col tw-items-center tw-px-6 tw-pb-10 tw-pt-10 tw-text-center md:tw-px-10">{t12}{t13}{t21}{t22}{t23}</div></section></div></div></div></div>;
    $[38] = t12;
    $[39] = t13;
    $[40] = t21;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  return t24;
}
export default function PublicWaveShell(t0) {
  const $ = _c(4);
  const {
    waveId
  } = t0;
  const shellState = usePublicWaveShellState(waveId);
  switch (shellState.status) {
    case "loading":
      {
        let t1;
        if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PublicWaveLoadingState />;
          $[0] = t1;
        } else {
          t1 = $[0];
        }
        return t1;
      }
    case "unavailable":
      {
        let t1;
        if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
          t1 = <PublicWaveUnavailableState />;
          $[1] = t1;
        } else {
          t1 = $[1];
        }
        return t1;
      }
    case "ready":
      {
        let t1;
        if ($[2] !== shellState.wave) {
          t1 = <PublicWaveShellContent wave={shellState.wave} />;
          $[2] = shellState.wave;
          $[3] = t1;
        } else {
          t1 = $[3];
        }
        return t1;
      }
  }
}